import { inject, Injectable } from "@angular/core";
import { ApiClientConstant } from "@cureskin/api-client";
import { TimeService } from "@services/time-service";

@Injectable({ providedIn: 'root' })
export class OrderService {
  private readonly timeService: TimeService = inject(TimeService);

  getLatestDeliveredRegimenOrder(orders: Table.Order[]): Table.Order | null {
    const { REGIMEN } = ApiClientConstant.Order.Type;
    const { DELIVERED } = ApiClientConstant.Order.Stage;
    const deliveredRegimenOrders = (orders ?? []).filter(
      (o: any) => o?.get?.('type') === REGIMEN && o?.get?.('stage') === DELIVERED,
    );
    if (!deliveredRegimenOrders.length) return null;

    return deliveredRegimenOrders.sort((a: any, b: any) => {
      const aDate = new Date(a?.get?.('deliveredOn') ?? a?.deliveredOn).getTime();
      const bDate = new Date(b?.get?.('deliveredOn') ?? b?.deliveredOn).getTime();
      return bDate - aDate;
    })[0];
  }

  getDeliveredOn(order: any): Date {
    return new Date(order?.get?.('deliveredOn') ?? order?.deliveredOn);
  }

  getDaysSinceDelivered(order: any): number {
    return this.timeService.differenceInDays(new Date(), this.getDeliveredOn(order));
  }

  shouldShowPartialOrderCancellationUI(order: Table.Order, isExperimentEnabled: boolean): boolean {
    if(!order) return false;
    if (order.get('type') !== ApiClientConstant.Order.Type.PRODUCT) return false;
    if (!isExperimentEnabled) return false;
    if (!order.get('productApprovalDetails')?.productDetails?.length) return false;

    const cancelledProducts = this.getCancelledProducts(order.get('productApprovalDetails') as any)
    if (!cancelledProducts.length) return false;
    return true;
  }

  getCancelledProducts(productApprovalDetails: Table.Order['attributes']['productApprovalDetails']): Array<Table.Order['attributes']['productApprovalDetails']['productDetails'][number]> | [] {
    if (!productApprovalDetails?.productDetails?.length) return [];
    const filteredProducts = productApprovalDetails.productDetails.filter((productDetails) => productDetails.status === ApiClientConstant.Order.ProductApprovalStatus.REJECTED);
    return filteredProducts;
  }

  initializeProductApprovalData(order: Table.Order, isPartialCancellationUIEnabled: boolean): {
    cancelledProducts: Array<Table.Order['attributes']['productApprovalDetails']['productDetails'][number]>;
    orderedProductsCount: number;
    shouldShowPartialOrderCancellationUI: boolean;
    cancelledProductsCount: number;
    totalProductsCount: number;
  } {
    if (!order) {
      return {
        cancelledProducts: [],
        orderedProductsCount: 0,
        shouldShowPartialOrderCancellationUI: false,
        cancelledProductsCount: 0,
        totalProductsCount: 0,
      };
    }

    const cancelledProducts = this.getCancelledProducts(order.get('productApprovalDetails'));
    const orderedProductsCount = order.get('products')?.length || 0;
    const shouldShowPartialOrderCancellationUI = this.shouldShowPartialOrderCancellationUI(order, isPartialCancellationUIEnabled);
    const cancelledProductsCount = cancelledProducts.length;
    const totalProductsCount = orderedProductsCount + cancelledProductsCount;

    return {
      cancelledProducts,
      orderedProductsCount,
      shouldShowPartialOrderCancellationUI,
      cancelledProductsCount,
      totalProductsCount,
    };
  }

  shouldHideProductDetails(order: Table.Order, cancelledProducts: Array<Table.Order['attributes']['productApprovalDetails']['productDetails'][number]>): boolean {
    const cancelledStages: string[] = [
      ApiClientConstant.Order.Stage.CANCELED,
      ApiClientConstant.Order.Stage.RETURN_REQUESTED,
      ApiClientConstant.Order.Stage.REFUND_INITIATED,
      ApiClientConstant.Order.Stage.REFUND_PROCESSED,
      ApiClientConstant.Order.Stage.RETURNED,
      ApiClientConstant.Order.Stage.LOST_IN_TRANSIT,
      ApiClientConstant.Order.Stage.COURIER_NOT_AVAILABLE,
      ApiClientConstant.Order.Stage.BOT_CANCELED,
    ]
    if (!cancelledStages.includes(order.get('stage'))) {
      return false;
    }
    if (!cancelledProducts.length) {
      return false;
    }
    if (cancelledProducts.length !== order.get('productInfo')?.length) {
      return false;
    }
    return true;
  }
}
